import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Calendar, Clock, ArrowRight, User } from 'lucide-react';
import { Lead, UserRole } from '../types';
import { motion } from 'motion/react';
import Tooltip from './Tooltip';

interface UpcomingDeadlinesProps {
  leads: Lead[];
  onEditLead: (lead: Lead) => void;
  userRole?: UserRole;
  selectedOwner?: string;
  selectedOwnerId?: string;
  isLoading?: boolean;
}

export function filterUpcomingLeads(
  leads: Lead[],
  selectedOwner?: string,
  selectedOwnerId?: string,
  currentDate: Date = new Date()
): Lead[] {
  const activeOwner = (selectedOwner || selectedOwnerId || '').trim();
  // Start of today (00:00:00 local time)
  const startOfToday = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
  // End of 7 days from today (23:59:59 local time)
  const endOf7Days = new Date(startOfToday.getTime() + 7 * 24 * 60 * 60 * 1000 + (24 * 60 * 60 * 1000 - 1));

  return leads
    .filter((lead) => {
      // Exclude archived leads or leads without follow-up dates
      if (lead.isArchived) return false;
      if (!lead.followUpDate) return false;
      // Completed follow-ups drop off the board
      if (lead.followUpCompleted || lead.followUpCompletedAt) return false;

      // Filter by selected owner if one is specified (bypass when "All Owners" / empty)
      if (activeOwner) {
        const leadOwner = (lead.owner || '').trim();
        const leadOwnerId = ((lead as any).ownerId || '').trim();
        if (leadOwner !== activeOwner && leadOwnerId !== activeOwner) {
          return false;
        }
      }

      const dueDate = new Date(lead.followUpDate + 'T00:00:00');
      // Include any that are due today, within next 7 days, or overdue
      return dueDate <= endOf7Days;
    })
    .sort((a, b) => {
      const dateA = new Date(a.followUpDate! + 'T00:00:00').getTime();
      const dateB = new Date(b.followUpDate! + 'T00:00:00').getTime();
      return dateA - dateB;
    });
}

export default function UpcomingDeadlines({
  leads,
  onEditLead,
  userRole,
  selectedOwner,
  selectedOwnerId,
  isLoading,
}: UpcomingDeadlinesProps) {
  const canViewFinancials = userRole !== 'warehouse';
  const activeOwner = (selectedOwner || selectedOwnerId || '').trim();

  // Watcher on owner filter state to trigger a smooth loading skeleton transition on change
  const [isFiltering, setIsFiltering] = useState(false);
  const prevOwnerRef = useRef(activeOwner);

  useEffect(() => {
    if (prevOwnerRef.current !== activeOwner) {
      prevOwnerRef.current = activeOwner;
      setIsFiltering(true);
      const timer = setTimeout(() => {
        setIsFiltering(false);
      }, 180);
      return () => clearTimeout(timer);
    }
  }, [activeOwner]);

  const showLoading = Boolean(isLoading || isFiltering);

  // Filter leads with follow-up dates in the next 7 days (or overdue/due today), matching the selected owner
  const upcomingLeads = useMemo(() => {
    return filterUpcomingLeads(leads, selectedOwner, selectedOwnerId);
  }, [leads, selectedOwner, selectedOwnerId]);

  const getRelativeBadge = (dateStr: string) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const target = new Date(dateStr + 'T00:00:00');
    
    const diffTime = target.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return {
        label: `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'}`,
        classes: 'text-rose-700 bg-rose-50 dark:bg-rose-950/40 dark:text-rose-300 border border-rose-100 dark:border-rose-900/40',
      };
    } else if (diffDays === 0) {
      return {
        label: 'Due Today',
        classes: 'text-amber-700 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-100 dark:border-amber-900/40 font-black animate-pulse',
      };
    } else if (diffDays === 1) {
      return {
        label: 'Due Tomorrow',
        classes: 'text-blue-700 bg-blue-50 dark:bg-blue-950/40 dark:text-blue-300 border border-blue-100 dark:border-blue-900/40 font-bold',
      };
    } else {
      return {
        label: `Due in ${diffDays} days`,
        classes: 'text-slate-700 bg-slate-50 dark:bg-slate-900 dark:text-slate-350 border border-slate-200 dark:border-slate-800 font-semibold',
      };
    }
  };

  return (
    <div className="mb-8">
      {/* Widget Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-blue-500/10 rounded-lg text-blue-600 dark:text-blue-400">
            <Clock className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-800 dark:text-slate-100 tracking-tight flex items-center gap-2">
              Upcoming Deadlines & Follow-ups
              <Tooltip text="Outreach, quotes, and follow-ups that are overdue or scheduled within the next 7 days." />
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Pipeline tasks & customer outreach due within the next 7 days
            </p>
          </div>
        </div>
        <span className="text-xs bg-slate-100 dark:bg-slate-950 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-800 font-mono font-bold px-2.5 py-1 rounded-full">
          {showLoading ? (
            <span className="inline-block w-16 h-3 bg-slate-300 dark:bg-slate-700 rounded animate-pulse align-middle" />
          ) : (
            `${upcomingLeads.length} Action Item${upcomingLeads.length === 1 ? '' : 's'}`
          )}
        </span>
      </div>

      {/* Widget Body */}
      {showLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg p-2 h-[135px] shadow-sm animate-pulse flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="h-3.5 w-16 bg-slate-200 dark:bg-slate-800 rounded"></div>
                  <div className="h-3.5 w-12 bg-slate-200 dark:bg-slate-800 rounded"></div>
                </div>
                <div className="h-3 w-28 bg-slate-200 dark:bg-slate-800 rounded mb-1.5"></div>
                <div className="h-2.5 w-20 bg-slate-100 dark:bg-slate-900 rounded mb-2"></div>
                <div className="h-5 w-full bg-slate-100 dark:bg-slate-900/60 rounded"></div>
              </div>
              <div className="pt-1 border-t border-slate-100 dark:border-slate-900 flex items-center justify-between">
                <div className="h-2.5 w-16 bg-slate-200 dark:bg-slate-800 rounded"></div>
                <div className="h-2.5 w-12 bg-slate-200 dark:bg-slate-800 rounded"></div>
              </div>
            </div>
          ))}
        </div>
      ) : upcomingLeads.length === 0 ? (
        <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800/80 rounded-xl p-8 text-center shadow-sm flex flex-col items-center justify-center">
          <Calendar className="w-8 h-8 text-slate-300 dark:text-slate-700 mb-2" />
          <p className="text-xs font-bold text-slate-700 dark:text-slate-300">
            {activeOwner ? `No pressing deadlines for ${activeOwner}` : 'No pressing deadlines'}
          </p>
          <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-1 max-w-sm">
            {activeOwner
              ? `All leads assigned to ${activeOwner} are up to date. No customer follow-ups or quote deadlines scheduled for the next 7 days.`
              : 'All your leads are accounted for. No customer follow-ups or quote deadlines scheduled for the next 7 days.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {upcomingLeads.map((lead) => {
            const badge = getRelativeBadge(lead.followUpDate!);
            return (
              <motion.div
                key={lead.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ y: -2 }}
                onClick={() => onEditLead(lead)}
                className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 hover:border-blue-400 dark:hover:border-blue-500 rounded-lg p-2 h-[135px] shadow-sm hover:shadow-md transition duration-150 cursor-pointer flex flex-col justify-between group relative overflow-hidden"
              >
                {/* Visual marker */}
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500" />

                <div>
                  {/* Relative Day Badge & Deal Value */}
                  <div className="flex items-center justify-between mb-1 pl-1">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-mono ${badge.classes}`}>
                      {badge.label}
                    </span>
                    {canViewFinancials && (
                      <span className="text-[10px] font-mono font-black text-slate-700 dark:text-slate-300">
                        ${lead.dealValue.toLocaleString()}
                      </span>
                    )}
                  </div>

                  {/* Company & Lead Name */}
                  <div className="pl-1">
                    <h4 className="text-[11px] font-extrabold text-slate-800 dark:text-slate-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition line-clamp-1 leading-tight" title={lead.name}>
                      {lead.name}
                    </h4>
                    <p className="text-[10px] text-slate-500 dark:text-slate-400 line-clamp-1" title={lead.company}>
                      {lead.company}
                    </p>
                  </div>

                  {/* Next Steps / Follow-Up Notes preview */}
                  {(lead.followUpNotes || lead.nextSteps) && (
                    <div className="mt-1.5 p-1 bg-slate-50 dark:bg-slate-900/60 rounded text-[9px] text-slate-600 dark:text-slate-400 pl-1.5">
                      <p className="font-semibold text-slate-700 dark:text-slate-300 text-[8px] uppercase tracking-wider mb-0.5 leading-none">
                        {lead.followUpNotes ? 'Follow-Up Notes:' : 'Next Steps:'}
                      </p>
                      <p className="line-clamp-2 leading-tight" title={lead.followUpNotes || lead.nextSteps}>
                        {lead.followUpNotes || lead.nextSteps}
                      </p>
                    </div>
                  )}
                </div>

                {/* Bottom Details */}
                <div className="mt-2 pt-1 border-t border-slate-100 dark:border-slate-900 flex items-center justify-between text-[10px] text-slate-500 pl-1">
                  <div className="flex items-center gap-1">
                    <User className="w-2.5 h-2.5 text-slate-400" />
                    <span className="truncate max-w-[90px] font-medium text-slate-600 dark:text-slate-400" title={lead.owner || 'Unassigned'}>
                      {lead.owner || 'Unassigned'}
                    </span>
                  </div>
                  <span className="text-blue-600 dark:text-blue-400 font-bold flex items-center gap-0.5 group-hover:translate-x-0.5 transition-transform">
                    Outreach <ArrowRight className="w-2.5 h-2.5" />
                  </span>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}
